import {
  DEFAULT_OPENCLAW_GATEWAY_PORT,
  DEFAULT_ORGX_BASE_URL,
} from "../constants.js";
import { isRecord, parseJsonObject, readObject } from "../lib/fs.js";

type JsonObject = Record<string, unknown>;

export interface OpenClawInspection {
  configured: boolean;
  installed: boolean;
  pluginKey: "orgx" | "openclaw-plugin" | null;
  details: string[];
}

export interface OpenClawAuthConfig {
  apiKey: string;
  baseUrl: string;
  pluginKey: "orgx" | "openclaw-plugin" | null;
  enabled: boolean;
}

export interface OpenClawOrgxDefaultsInput {
  apiKey?: string;
  baseUrl?: string;
}

function cleanupEmptyObjects(value: JsonObject): void {
  for (const [key, child] of Object.entries(value)) {
    if (!isRecord(child)) continue;
    cleanupEmptyObjects(child);
    if (Object.keys(child).length === 0) {
      delete value[key];
    }
  }
}

export function readOpenClawGatewayPort(raw: unknown): number {
  const isValidPort = (value: number): boolean =>
    Number.isFinite(value) && value >= 1 && value <= 65_535;

  const root = isRecord(raw) ? raw : {};
  const gateway = readObject(root.gateway);
  const port = gateway.port;

  if (typeof port === "number" && isValidPort(port)) {
    return Math.floor(port);
  }

  if (typeof port === "string") {
    const parsed = Number.parseInt(port, 10);
    if (isValidPort(parsed)) {
      return parsed;
    }
  }

  return DEFAULT_OPENCLAW_GATEWAY_PORT;
}

export function buildLocalMcpUrl(raw: unknown): string {
  return `http://127.0.0.1:${readOpenClawGatewayPort(raw)}/orgx/mcp`;
}

function resolvePluginKey(entries: JsonObject): "orgx" | "openclaw-plugin" {
  if (isRecord(entries["openclaw-plugin"])) {
    return "openclaw-plugin";
  }

  if (isRecord(entries.orgx)) {
    return "orgx";
  }

  return "orgx";
}

export function readOpenClawAuth(input: string | null): OpenClawAuthConfig {
  const current = parseJsonObject(input);
  const plugins = readObject(current.plugins);
  const entries = readObject(plugins.entries);
  const pluginKey = isRecord(entries["openclaw-plugin"])
    ? "openclaw-plugin"
    : isRecord(entries.orgx)
      ? "orgx"
      : null;
  const entry = pluginKey ? readObject(entries[pluginKey]) : {};
  const config = readObject(entry.config);

  return {
    apiKey: typeof config.apiKey === "string" ? config.apiKey.trim() : "",
    baseUrl:
      typeof config.baseUrl === "string" && config.baseUrl.trim().length > 0
        ? config.baseUrl.trim()
        : DEFAULT_ORGX_BASE_URL,
    pluginKey,
    enabled: entry.enabled !== false,
  };
}

export function patchOpenClawConfig(
  input: string | null,
  auth: OpenClawOrgxDefaultsInput = {},
): string {
  const current = parseJsonObject(input);
  const plugins = readObject(current.plugins);
  const entries = readObject(plugins.entries);
  const pluginKey = resolvePluginKey(entries);
  const entry = readObject(entries[pluginKey]);
  const config = readObject(entry.config);
  const apiKey =
    typeof auth.apiKey === "string" && auth.apiKey.trim().length > 0
      ? auth.apiKey.trim()
      : typeof config.apiKey === "string" && config.apiKey.trim().length > 0
        ? config.apiKey.trim()
        : undefined;
  const baseUrl =
    typeof auth.baseUrl === "string" && auth.baseUrl.trim().length > 0
      ? auth.baseUrl.trim()
      : typeof config.baseUrl === "string" && config.baseUrl.trim().length > 0
        ? config.baseUrl.trim()
        : DEFAULT_ORGX_BASE_URL;

  entries[pluginKey] = {
    ...entry,
    enabled: true,
    config: {
      ...config,
      ...(apiKey ? { apiKey } : {}),
      baseUrl,
      dashboardEnabled:
        typeof config.dashboardEnabled === "boolean"
          ? config.dashboardEnabled
          : true,
      autoInstallAgentSuiteOnConnect:
        typeof config.autoInstallAgentSuiteOnConnect === "boolean"
          ? config.autoInstallAgentSuiteOnConnect
          : true,
      autoConfigureMcpClientsOnConnect:
        typeof config.autoConfigureMcpClientsOnConnect === "boolean"
          ? config.autoConfigureMcpClientsOnConnect
          : true,
    },
  };

  plugins.entries = entries;
  current.plugins = plugins;

  return `${JSON.stringify(current, null, 2)}\n`;
}

export function removeOpenClawConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const plugins = readObject(current.plugins);
  const entries = readObject(plugins.entries);

  for (const key of ["openclaw-plugin", "orgx"]) {
    const entry = readObject(entries[key]);
    if (!Object.keys(entry).length) continue;
    const config = readObject(entry.config);
    delete config.baseUrl;
    delete config.dashboardEnabled;
    delete config.autoInstallAgentSuiteOnConnect;
    delete config.autoConfigureMcpClientsOnConnect;
    if (Object.keys(config).length > 0) {
      entry.config = config;
    } else {
      delete entry.config;
    }
    entries[key] = entry;
  }

  plugins.entries = entries;
  current.plugins = plugins;
  cleanupEmptyObjects(current);

  return `${JSON.stringify(current, null, 2)}\n`;
}

export function inspectOpenClawConfig(input: string | null): OpenClawInspection {
  const current = parseJsonObject(input);
  const plugins = readObject(current.plugins);
  const entries = readObject(plugins.entries);
  const installs = readObject(plugins.installs);
  const pluginKey = isRecord(entries["openclaw-plugin"])
    ? "openclaw-plugin"
    : isRecord(entries.orgx)
      ? "orgx"
      : null;
  const entry = pluginKey ? readObject(entries[pluginKey]) : {};
  const config = readObject(entry.config);
  const installed =
    pluginKey !== null ||
    isRecord(installs["openclaw-plugin"]) ||
    isRecord(installs.orgx);
  const configured =
    installed &&
    entry.enabled === true &&
    (typeof config.baseUrl === "string" ||
      typeof config.autoInstallAgentSuiteOnConnect === "boolean" ||
      typeof config.autoConfigureMcpClientsOnConnect === "boolean");

  return {
    configured,
    installed,
    pluginKey,
    details: [
      `plugin key: ${pluginKey ?? "missing"}`,
      `enabled: ${entry.enabled === true ? "yes" : "no"}`,
      `gateway port: ${readOpenClawGatewayPort(current)}`,
    ],
  };
}
